import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { LOGO_URL, REGIONS, CATEGORY_LABELS } from "@/lib/constants";
import { MemberCard } from "@/components/MemberCard";
import { toast } from "sonner";
import {
  CreditCard, User, Calendar, Receipt, FileText, LogOut, Home,
  Loader2, CheckCircle2, MapPin, Save, Download, Wallet,
} from "lucide-react";

const NAV = [
  { key: "card", label: "Ma carte", Icon: CreditCard },
  { key: "profile", label: "Mon profil", Icon: User },
  { key: "events", label: "Événements", Icon: Calendar },
  { key: "contributions", label: "Contributions", Icon: Receipt },
  { key: "documents", label: "Documents", Icon: FileText },
];

const STATUS_LABELS = {
  en_attente: { label: "En attente de validation", cls: "bg-amber-100 text-amber-700" },
  valide: { label: "Validé", cls: "bg-emerald-100 text-emerald-700" },
  rejete: { label: "Rejeté", cls: "bg-red-100 text-red-700" },
};

export default function MemberSpace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(
    params.get("session_id") || params.get("payment") ? "contributions" : "card"
  );
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/member/me").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const doLogout = () => { logout(); navigate("/espace-membre/connexion"); };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand-blue" size={32} /></div>;

  const membership = data.membership;
  const status = STATUS_LABELS[membership?.status] || STATUS_LABELS.en_attente;

  return (
    <div data-testid="member-space" className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-brand-blue text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="DEFAR" className="h-10 bg-white p-1 rounded-sm" />
            <div>
              <p className="font-bold leading-tight">DEFAR & Moi</p>
              <p className="text-xs text-blue-200">Bonjour, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-2 text-sm text-blue-100 hover:text-white px-3 py-2"><Home size={16} /> Site public</button>
            <button onClick={doLogout} data-testid="member-logout" className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-3 py-2 rounded-sm"><LogOut size={16} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <nav className="bg-white border border-border rounded-sm p-2 flex lg:flex-col gap-1 overflow-x-auto">
            {NAV.map(({ key, label, Icon }) => (
              <button key={key} data-testid={`member-tab-${key}`} onClick={() => setTab(key)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-sm whitespace-nowrap transition-colors ${tab === key ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                <Icon size={18} /> {label}
              </button>
            ))}
          </nav>
          <div className="mt-4 bg-white border border-border rounded-sm p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Statut adhésion</p>
            <span className={`mt-2 inline-block text-xs font-semibold px-2.5 py-1 rounded ${status.cls}`}>{status.label}</span>
          </div>
        </aside>

        {/* Content */}
        <main className="lg:col-span-9">
          {tab === "card" && (
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-6">Ma carte de membre</h1>
              <div className="max-w-md">
                <MemberCard membership={membership} name={user?.name} />
              </div>
            </div>
          )}
          {tab === "profile" && <ProfileTab membership={membership} user={user} onUpdate={(u) => setData({ ...data, user: u })} />}
          {tab === "events" && <EventsTab />}
          {tab === "contributions" && <ContributionsTab />}
          {tab === "documents" && <DocumentsTab />}
        </main>
      </div>
    </div>
  );
}

function ProfileTab({ membership, user, onUpdate }) {
  const [form, setForm] = useState({
    full_name: membership?.full_name || user?.name || "",
    phone: membership?.phone || "",
    region: membership?.region || "",
    city: membership?.city || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/member/profile", form);
      onUpdate(data);
      toast.success("Profil mis à jour.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Mon profil</h1>
      <form onSubmit={save} className="bg-white border border-border rounded-sm p-6 max-w-xl space-y-5">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
          <input value={membership?.email || user?.email || ""} disabled className="w-full border border-border px-4 py-2.5 rounded-sm text-sm bg-slate-50 text-slate-500" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nom complet</label>
          <input value={form.full_name} onChange={set("full_name")} data-testid="profile-name" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Téléphone</label>
            <input value={form.phone} onChange={set("phone")} data-testid="profile-phone" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Région</label>
            <select value={form.region} onChange={set("region")} data-testid="profile-region" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue">
              <option value="">Choisir</option>
              {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ville</label>
          <input value={form.city} onChange={set("city")} data-testid="profile-city" className="w-full border border-border px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-brand-blue" />
        </div>
        <button type="submit" disabled={loading} data-testid="profile-save" className="inline-flex items-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-6 py-3 font-semibold disabled:opacity-60">
          <Save size={16} /> {loading ? "Enregistrement..." : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/member/events").then(({ data }) => setEvents(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    try {
      const { data } = await api.post(`/member/events/${id}/toggle`);
      setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, registered: data.registered } : e)));
      toast.success(data.registered ? "Inscription confirmée !" : "Inscription annulée.");
    } catch (err) {
      toast.error("Action impossible.");
    }
  };

  if (loading) return <Loader />;
  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Événements & missions</h1>
      <div className="space-y-3">
        {events.map((e) => (
          <div key={e.id} data-testid={`member-event-${e.id}`} className="bg-white border border-border rounded-sm p-5 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-900">{e.title}</p>
              <p className="text-sm text-slate-500 flex items-center gap-2 mt-1"><Calendar size={14} /> {new Date(e.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} · <MapPin size={14} /> {e.location}</p>
            </div>
            <button onClick={() => toggle(e.id)} data-testid={`toggle-event-${e.id}`}
              className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors ${e.registered ? "bg-brand-green text-white" : "border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white"}`}>
              {e.registered ? <><CheckCircle2 size={16} /> Inscrit</> : "S'inscrire"}
            </button>
          </div>
        ))}
        {events.length === 0 && <Empty label="Aucun événement à venir." />}
      </div>
    </div>
  );
}

function ContributionsTab() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadContribs = () => api.get("/member/contributions").then(({ data }) => setItems(data));

  useEffect(() => {
    Promise.all([
      loadContribs(),
      api.get("/payments/packages").then(({ data }) => setPackages(data)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const sessionId = params.get("session_id");
    const payment = params.get("payment");
    if (payment === "cancel") {
      toast.info("Paiement annulé.");
      setParams({}, { replace: true });
    } else if (sessionId) {
      setVerifying(true);
      pollStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollStatus = async (sessionId, attempts) => {
    if (attempts >= 6) {
      setVerifying(false);
      toast.error("Vérification du paiement expirée. Vérifiez votre historique.");
      setParams({}, { replace: true });
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false);
        toast.success("Paiement réussi ! Merci pour votre contribution.");
        await loadContribs();
        setParams({}, { replace: true });
        return;
      }
      if (data.status === "expired") {
        setVerifying(false);
        toast.error("Session de paiement expirée.");
        setParams({}, { replace: true });
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setVerifying(false);
      toast.error("Erreur lors de la vérification du paiement.");
    }
  };

  const pay = async (packageId) => {
    setPaying(packageId);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setPaying(null);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <Loader />;
  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Mes contributions</h1>

      {verifying && (
        <div data-testid="payment-verifying" className="mb-6 bg-blue-50 border border-blue-200 rounded-sm p-4 flex items-center gap-3 text-brand-blue">
          <Loader2 className="animate-spin" size={20} /> Vérification de votre paiement en cours...
        </div>
      )}

      {/* Payment packages */}
      <div className="bg-white border border-border rounded-sm p-6 mb-8">
        <h2 className="font-bold text-slate-900">Cotiser / Faire un don</h2>
        <p className="mt-1 text-sm text-slate-500">Soutenez le mouvement par carte bancaire (paiement sécurisé via Stripe).</p>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {packages.map((p) => (
            <button
              key={p.id}
              data-testid={`pay-${p.id}`}
              onClick={() => pay(p.id)}
              disabled={!!paying}
              className="border-2 border-border hover:border-brand-green hover:bg-brand-green/5 transition-colors p-4 text-center disabled:opacity-60"
            >
              <Wallet size={20} className="mx-auto text-brand-green" />
              <p className="mt-2 text-lg font-bold text-slate-900">{p.amount} €</p>
              <p className="text-xs text-slate-500 leading-tight">{p.label}</p>
              {paying === p.id && <Loader2 className="animate-spin mx-auto mt-2 text-brand-green" size={16} />}
            </button>
          ))}
        </div>
      </div>

      {/* History */}
      <h2 className="font-bold text-slate-900 mb-3">Historique</h2>
      {items.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 text-center">
          <Receipt size={36} className="mx-auto text-slate-300" />
          <p className="mt-4 font-semibold text-slate-700">Aucune contribution enregistrée</p>
          <p className="mt-1 text-sm text-slate-500">Vos reçus apparaîtront ici après votre premier paiement.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-sm divide-y divide-border" data-testid="contributions-list">
          {items.map((c, i) => (
            <div key={i} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={18} className="text-brand-green" />
                <div>
                  <p className="font-medium text-slate-900">{c.label}</p>
                  <p className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
              </div>
              <span className="font-semibold text-brand-green">{c.amount} {(c.currency || "eur").toUpperCase()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/member/documents").then(({ data }) => setDocs(data)).finally(() => setLoading(false)); }, []);
  if (loading) return <Loader />;
  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Documents internes</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {docs.map((d, i) => (
          <div key={i} className="bg-white border border-border rounded-sm p-5 flex items-start gap-4">
            <div className="h-11 w-11 flex items-center justify-center bg-brand-blue/5 text-brand-blue shrink-0"><FileText size={22} /></div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-brand-green font-bold">{d.category}</p>
              <p className="font-semibold text-slate-900">{d.title}</p>
              <button className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue"><Download size={14} /> {d.type}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Loader = () => <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-blue" size={28} /></div>;
const Empty = ({ label }) => <div className="bg-white border border-border rounded-sm py-16 text-center text-slate-400">{label}</div>;
